import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Loader2,
  Save,
  RefreshCw,
  Settings2,
  Brain,
  FileText,
  Eye,
  Sparkles,
  AlertCircle,
  CheckCircle,
  Info,
  Plus,
  Trash2,
  GripVertical,
  History,
  RotateCcw,
  Clock,
  BookmarkPlus,
  Mic,
  MessageSquare,
  ChevronDown
} from 'lucide-react';
import { VoiceAgentsManager } from '@/components/admin/VoiceAgentsManager';
import { format } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';

interface SystemSetting {
  id: string;
  setting_key: string;
  setting_value: any;
  category: string;
  description: string;
  is_sensitive: boolean;
  updated_at: string;
}

interface AIModel {
  id: string;
  name: string;
  provider: string;
  description: string;
}

interface EvaluationCriterion {
  criterion: string;
  weight: number;
  description: string;
}

interface PromptRevision {
  id: string;
  prompt: string;
  savedAt: string;
  savedBy: string;
  label?: string;
}

const MAX_REVISIONS = 10;

export default function SystemConfiguration() {
  const { user } = useAuth();
  const [settings, setSettings] = useState<Record<string, SystemSetting>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [availableModels, setAvailableModels] = useState<AIModel[]>([]);
  
  // Revision dialog state
  const [revisionDialogOpen, setRevisionDialogOpen] = useState(false);
  const [revisionSettingKey, setRevisionSettingKey] = useState<string | null>(null);
  const [revisionLabel, setRevisionLabel] = useState('');

  // Local state for editing
  const [editedSettings, setEditedSettings] = useState<Record<string, any>>({});

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      // Use type assertion since system_settings table is not in generated types yet
      const { data, error } = await (supabase as any)
        .from('system_settings')
        .select('*')
        .order('category', { ascending: true });

      if (error) throw error;

      const settingsMap: Record<string, SystemSetting> = {};
      (data as SystemSetting[])?.forEach((setting) => {
        settingsMap[setting.setting_key] = setting;
      });

      setSettings(settingsMap);

      // Initialize edited settings with current values
      const edited: Record<string, any> = {};
      (data as SystemSetting[])?.forEach((setting) => {
        edited[setting.setting_key] = setting.setting_value;
      });
      setEditedSettings(edited);

      // Extract available models
      if (settingsMap.available_ai_models?.setting_value?.models) {
        setAvailableModels(settingsMap.available_ai_models.setting_value.models);
      }

    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load system settings. Please ensure the system_settings table exists.');
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = (key: string, value: any) => {
    setEditedSettings(prev => ({
      ...prev,
      [key]: value
    }));
    setHasChanges(true);
  };

  const saveSetting = async (key: string) => {
    try {
      setSaving(true);
      const { error } = await (supabase as any)
        .from('system_settings')
        .update({ setting_value: editedSettings[key] })
        .eq('setting_key', key);

      if (error) throw error;

      setSettings(prev => ({
        ...prev,
        [key]: {
          ...prev[key],
          setting_value: editedSettings[key],
          updated_at: new Date().toISOString()
        }
      }));

      toast.success('Setting saved successfully');
    } catch (error) {
      console.error('Error saving setting:', error);
      toast.error('Failed to save setting');
    } finally {
      setSaving(false);
    }
  };

  const saveAllSettings = async () => {
    try {
      setSaving(true);
      
      // Auto-save revisions for prompt settings that have changed
      const promptKeys = ['evaluation_system_prompt', 'coaching_system_prompt'];
      for (const key of promptKeys) {
        const currentValue = settings[key]?.setting_value;
        const newValue = editedSettings[key];
        
        // If prompt has changed, auto-save previous version as revision
        if (currentValue?.prompt && newValue?.prompt && currentValue.prompt !== newValue.prompt) {
          await saveRevisionForKey(key, 'Auto-saved before update');
        }
      }
      
      const updates = Object.entries(editedSettings).map(([key, value]) => ({
        setting_key: key,
        setting_value: value
      }));

      for (const update of updates) {
        const { error } = await (supabase as any)
          .from('system_settings')
          .update({ setting_value: update.setting_value })
          .eq('setting_key', update.setting_key);

        if (error) throw error;
      }

      await fetchSettings();
      setHasChanges(false);
      toast.success('All settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Save a revision for a specific setting
  const saveRevisionForKey = async (settingKey: string, label?: string) => {
    const currentSetting = settings[settingKey];
    if (!currentSetting?.setting_value?.prompt) return;

    const currentPrompt = currentSetting.setting_value.prompt;
    const currentRevisions: PromptRevision[] = currentSetting.setting_value.revisions || [];
    
    const newRevision: PromptRevision = {
      id: crypto.randomUUID(),
      prompt: currentPrompt,
      savedAt: new Date().toISOString(),
      savedBy: user?.email || 'Unknown',
      label: label || undefined
    };

    // Add new revision and keep only the last MAX_REVISIONS
    const updatedRevisions = [newRevision, ...currentRevisions].slice(0, MAX_REVISIONS);

    const updatedValue = {
      ...currentSetting.setting_value,
      revisions: updatedRevisions
    };

    const { error } = await (supabase as any)
      .from('system_settings')
      .update({ setting_value: updatedValue })
      .eq('setting_key', settingKey);

    if (error) throw error;

    // Update local state
    setSettings(prev => ({
      ...prev,
      [settingKey]: {
        ...prev[settingKey],
        setting_value: updatedValue
      }
    }));
    setEditedSettings(prev => ({
      ...prev,
      [settingKey]: updatedValue
    }));
  };

  const handleSaveRevision = async () => {
    if (!revisionSettingKey) return;
    
    try {
      await saveRevisionForKey(revisionSettingKey, revisionLabel || undefined);
      toast.success('Revision saved successfully');
      setRevisionDialogOpen(false);
      setRevisionLabel('');
      setRevisionSettingKey(null);
    } catch (error) {
      console.error('Error saving revision:', error);
      toast.error('Failed to save revision');
    }
  };

  const loadRevision = (settingKey: string, revisionId: string) => {
    const currentSetting = editedSettings[settingKey];
    const revisions: PromptRevision[] = currentSetting?.revisions || [];
    const revision = revisions.find(r => r.id === revisionId);
    
    if (revision) {
      setEditedSettings(prev => ({
        ...prev,
        [settingKey]: {
          ...prev[settingKey],
          prompt: revision.prompt
        }
      }));
      setHasChanges(true);
      toast.success('Revision loaded - remember to save changes');
    }
  };

  const deleteRevision = async (settingKey: string, revisionId: string) => {
    const currentSetting = editedSettings[settingKey];
    const revisions: PromptRevision[] = currentSetting?.revisions || [];
    const updatedRevisions = revisions.filter(r => r.id !== revisionId);

    const updatedValue = {
      ...currentSetting,
      revisions: updatedRevisions
    };

    try {
      const { error } = await (supabase as any)
        .from('system_settings')
        .update({ setting_value: updatedValue })
        .eq('setting_key', settingKey);

      if (error) throw error;

      setSettings(prev => ({
        ...prev,
        [settingKey]: {
          ...prev[settingKey],
          setting_value: updatedValue
        }
      }));
      setEditedSettings(prev => ({
        ...prev,
        [settingKey]: updatedValue
      }));
      toast.success('Revision deleted');
    } catch (error) {
      console.error('Error deleting revision:', error);
      toast.error('Failed to delete revision');
    }
  };

  const getModelName = (modelId: string) => {
    const model = availableModels.find(m => m.id === modelId);
    return model?.name || modelId;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <span className="ml-3">Loading system configuration...</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-foreground flex items-center gap-3">
                <Settings2 className="w-10 h-10 text-primary" />
                System Configuration
              </h1>
              <p className="text-lg text-muted-foreground mt-2">
                Configure AI models, prompts, and system-wide settings
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={fetchSettings} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button onClick={saveAllSettings} disabled={saving || !hasChanges}>
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save All Changes'}
              </Button>
            </div>
          </div>

          {hasChanges && (
            <Alert className="mt-4 border-yellow-500 bg-yellow-50 dark:bg-yellow-950">
              <AlertCircle className="h-4 w-4 text-yellow-600" />
              <AlertDescription className="text-yellow-800 dark:text-yellow-200">
                You have unsaved changes. Click "Save All Changes" to apply them.
              </AlertDescription>
            </Alert>
          )}
        </div>

        {/* Settings Tabs */}
        <Tabs defaultValue="evaluation" className="space-y-6">
          <TabsList className="grid w-full grid-cols-7 lg:w-auto lg:inline-grid">
            <TabsTrigger value="simulation" className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Simulation AI
            </TabsTrigger>
            <TabsTrigger value="evaluation" className="flex items-center gap-2">
              <Brain className="w-4 h-4" />
              Evaluation AI
            </TabsTrigger>
            <TabsTrigger value="coaching" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Coaching AI
            </TabsTrigger>
            <TabsTrigger value="content" className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              Content AI
            </TabsTrigger>
            <TabsTrigger value="vision" className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Vision AI
            </TabsTrigger>
            <TabsTrigger value="voice-agents" className="flex items-center gap-2">
              <Mic className="w-4 h-4" />
              Voice Agents
            </TabsTrigger>
            <TabsTrigger value="general" className="flex items-center gap-2">
              <Settings2 className="w-4 h-4" />
              General
            </TabsTrigger>
          </TabsList>

          {/* Simulation AI Settings — Tone & Disclosure Prompts */}
          <TabsContent value="simulation" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-primary" />
                  Simulation AI — Conversation Tone Prompts
                </CardTitle>
                <CardDescription>
                  Configure the personality prompts for each conversation tone. These control how the AI behaves during live simulations.
                  Leave a prompt empty to use the built-in default.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { key: 'tone_warm_professional', label: 'Warm Professional', emoji: '😊' },
                  { key: 'tone_neutral', label: 'Neutral / Matter-of-fact', emoji: '😐' },
                  { key: 'tone_empathetic', label: 'Empathetic', emoji: '🤗' },
                  { key: 'tone_skeptical', label: 'Skeptical', emoji: '🤨' },
                  { key: 'tone_dismissive', label: 'Dismissive', emoji: '😒' },
                  { key: 'tone_anxious', label: 'Anxious / Uncertain', emoji: '😰' },
                  { key: 'tone_overconfident', label: 'Overconfident', emoji: '😏' },
                  { key: 'tone_aggressive', label: 'Aggressive', emoji: '😠' },
                ].map(({ key, label, emoji }) => (
                  <details key={key} className="border rounded-lg">
                    <summary className="flex items-center gap-2 p-4 cursor-pointer hover:bg-muted/50">
                      <ChevronDown className="w-4 h-4 transition-transform" />
                      <span className="text-lg">{emoji}</span>
                      <span className="font-medium">{label}</span>
                      {editedSettings[key]?.prompt ? (
                        <Badge variant="default" className="ml-auto text-xs">Custom</Badge>
                      ) : (
                        <Badge variant="outline" className="ml-auto text-xs">Using Default</Badge>
                      )}
                    </summary>
                    <div className="px-4 pb-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-muted-foreground">
                          Custom prompt for the <strong>{label}</strong> tone. Leave empty to use the built-in default.
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setRevisionSettingKey(key);
                            setRevisionDialogOpen(true);
                          }}
                          className="gap-2"
                        >
                          <BookmarkPlus className="h-4 w-4" />
                          Save Revision
                        </Button>
                      </div>
                      <Textarea
                        value={editedSettings[key]?.prompt || ''}
                        onChange={(e) => updateSetting(key, {
                          ...editedSettings[key],
                          prompt: e.target.value
                        })}
                        className="min-h-[200px] font-mono text-sm"
                        placeholder={`Enter custom prompt for ${label} tone...\n\nExample:\nCONVERSATION TONE: ${label.toUpperCase()}\n- Instruction 1\n- Instruction 2`}
                      />
                      <PromptRevisionPanel
                        revisions={editedSettings[key]?.revisions || []}
                        onLoad={(revisionId) => loadRevision(key, revisionId)}
                        onDelete={(revisionId) => deleteRevision(key, revisionId)}
                      />
                    </div>
                  </details>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-primary" />
                  Simulation AI — Information Disclosure Prompts
                </CardTitle>
                <CardDescription>
                  Configure how much information the AI shares and how quickly. These control the AI's information-sharing behaviour during simulations.
                  Leave a prompt empty to use the built-in default.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { key: 'disclosure_minimal', label: 'Minimal', emoji: '🤐' },
                  { key: 'disclosure_guarded', label: 'Guarded', emoji: '🛡️' },
                  { key: 'disclosure_progressive', label: 'Progressive', emoji: '📈' },
                  { key: 'disclosure_direct', label: 'Direct', emoji: '➡️' },
                ].map(({ key, label, emoji }) => (
                  <details key={key} className="border rounded-lg">
                    <summary className="flex items-center gap-2 p-4 cursor-pointer hover:bg-muted/50">
                      <ChevronDown className="w-4 h-4 transition-transform" />
                      <span className="text-lg">{emoji}</span>
                      <span className="font-medium">{label}</span>
                      {editedSettings[key]?.prompt ? (
                        <Badge variant="default" className="ml-auto text-xs">Custom</Badge>
                      ) : (
                        <Badge variant="outline" className="ml-auto text-xs">Using Default</Badge>
                      )}
                    </summary>
                    <div className="px-4 pb-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-muted-foreground">
                          Custom prompt for <strong>{label}</strong> disclosure style. Leave empty to use the built-in default.
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setRevisionSettingKey(key);
                            setRevisionDialogOpen(true);
                          }}
                          className="gap-2"
                        >
                          <BookmarkPlus className="h-4 w-4" />
                          Save Revision
                        </Button>
                      </div>
                      <Textarea
                        value={editedSettings[key]?.prompt || ''}
                        onChange={(e) => updateSetting(key, {
                          ...editedSettings[key],
                          prompt: e.target.value
                        })}
                        className="min-h-[200px] font-mono text-sm"
                        placeholder={`Enter custom prompt for ${label} disclosure style...\n\nExample:\nINFORMATION DISCLOSURE: ${label.toUpperCase()}\n- Instruction 1\n- Instruction 2`}
                      />
                      <PromptRevisionPanel
                        revisions={editedSettings[key]?.revisions || []}
                        onLoad={(revisionId) => loadRevision(key, revisionId)}
                        onDelete={(revisionId) => deleteRevision(key, revisionId)}
                      />
                    </div>
                  </details>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Evaluation AI Settings */}
          <TabsContent value="evaluation" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="w-5 h-5 text-primary" />
                  Simulation Evaluation Settings
                </CardTitle>
                <CardDescription>
                  Configure the AI model and prompts used for evaluating simulation sessions
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Model Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>AI Model</Label>
                    <Select
                      value={editedSettings.evaluation_ai_model?.replace(/"/g, '') || ''}
                      onValueChange={(value) => updateSetting('evaluation_ai_model', `"${value}"`)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select model" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableModels.map(model => (
                          <SelectItem key={model.id} value={model.id}>
                            <div className="flex items-center gap-2">
                              <span>{model.name}</span>
                              <Badge variant="outline" className="text-xs">{model.provider}</Badge>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {settings.evaluation_ai_model?.description}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Temperature: {editedSettings.evaluation_temperature || 0.3}</Label>
                    <Slider
                      value={[parseFloat(editedSettings.evaluation_temperature) || 0.3]}
                      onValueChange={([value]) => updateSetting('evaluation_temperature', value.toString())}
                      min={0}
                      max={1}
                      step={0.1}
                      className="w-full"
                    />
                    <p className="text-xs text-muted-foreground">
                      Lower = more consistent, Higher = more creative
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Max Tokens</Label>
                  <Input
                    type="number"
                    value={editedSettings.evaluation_max_tokens || 4000}
                    onChange={(e) => updateSetting('evaluation_max_tokens', e.target.value)}
                  />
                </div>

                <Separator />

                {/* System Prompt */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-lg font-semibold">Evaluation System Prompt</Label>
                      <p className="text-sm text-muted-foreground">
                        This prompt instructs the AI how to evaluate simulation sessions. It defines the output format and scoring guidelines.
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setRevisionSettingKey('evaluation_system_prompt');
                        setRevisionDialogOpen(true);
                      }}
                      className="gap-2"
                    >
                      <BookmarkPlus className="h-4 w-4" />
                      Save Revision
                    </Button>
                  </div>
                  <Textarea
                    value={editedSettings.evaluation_system_prompt?.prompt || ''}
                    onChange={(e) => updateSetting('evaluation_system_prompt', { 
                      ...editedSettings.evaluation_system_prompt,
                      prompt: e.target.value 
                    })}
                    className="min-h-[400px] font-mono text-sm"
                    placeholder="Enter the system prompt for evaluation..."
                  />
                  
                  {/* Revisions Panel */}
                  <PromptRevisionPanel
                    revisions={editedSettings.evaluation_system_prompt?.revisions || []}
                    onLoad={(revisionId) => loadRevision('evaluation_system_prompt', revisionId)}
                    onDelete={(revisionId) => deleteRevision('evaluation_system_prompt', revisionId)}
                  />
                </div>

                <Separator />

                {/* Evaluation Criteria by Type */}
                <div className="space-y-4">
                  <Label className="text-lg font-semibold">Default Evaluation Criteria by Agent Type</Label>
                  <p className="text-sm text-muted-foreground">
                    These criteria are used when an agent doesn't have a custom rubric configured.
                  </p>

                  <Tabs defaultValue="patient" className="w-full">
                    <TabsList className="grid w-full grid-cols-3">
                      <TabsTrigger value="patient">Patient</TabsTrigger>
                      <TabsTrigger value="customer">Customer/Sales</TabsTrigger>
                      <TabsTrigger value="examiner">Examiner</TabsTrigger>
                    </TabsList>

                    {['patient', 'customer', 'examiner'].map(type => (
                      <TabsContent key={type} value={type} className="space-y-4">
                        <EvaluationCriteriaEditor
                          criteria={editedSettings[`evaluation_criteria_${type}`]?.criteria || []}
                          onChange={(criteria) => updateSetting(`evaluation_criteria_${type}`, { criteria })}
                        />
                      </TabsContent>
                    ))}
                  </Tabs>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Coaching AI Settings */}
          <TabsContent value="coaching" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  Coaching Document Generation Settings
                </CardTitle>
                <CardDescription>
                  Configure the AI model and prompts used for generating coaching session documents
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>AI Model</Label>
                    <Select
                      value={editedSettings.coaching_ai_model?.replace(/"/g, '') || ''}
                      onValueChange={(value) => updateSetting('coaching_ai_model', `"${value}"`)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select model" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableModels.map(model => (
                          <SelectItem key={model.id} value={model.id}>
                            <div className="flex items-center gap-2">
                              <span>{model.name}</span>
                              <Badge variant="outline" className="text-xs">{model.provider}</Badge>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Temperature: {editedSettings.coaching_temperature || 0.4}</Label>
                    <Slider
                      value={[parseFloat(editedSettings.coaching_temperature) || 0.4]}
                      onValueChange={([value]) => updateSetting('coaching_temperature', value.toString())}
                      min={0}
                      max={1}
                      step={0.1}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Max Tokens</Label>
                  <Input
                    type="number"
                    value={editedSettings.coaching_max_tokens || 8000}
                    onChange={(e) => updateSetting('coaching_max_tokens', e.target.value)}
                  />
                </div>

                <Separator />

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-lg font-semibold">Coaching Document System Prompt</Label>
                      <p className="text-sm text-muted-foreground">
                        This prompt instructs the AI how to generate coaching session documents.
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setRevisionSettingKey('coaching_system_prompt');
                        setRevisionDialogOpen(true);
                      }}
                      className="gap-2"
                    >
                      <BookmarkPlus className="h-4 w-4" />
                      Save Revision
                    </Button>
                  </div>
                  <Textarea
                    value={editedSettings.coaching_system_prompt?.prompt || ''}
                    onChange={(e) => updateSetting('coaching_system_prompt', { 
                      ...editedSettings.coaching_system_prompt,
                      prompt: e.target.value 
                    })}
                    className="min-h-[300px] font-mono text-sm"
                    placeholder="Enter the system prompt for coaching documents..."
                  />
                  
                  {/* Revisions Panel */}
                  <PromptRevisionPanel
                    revisions={editedSettings.coaching_system_prompt?.revisions || []}
                    onLoad={(revisionId) => loadRevision('coaching_system_prompt', revisionId)}
                    onDelete={(revisionId) => deleteRevision('coaching_system_prompt', revisionId)}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Content AI Settings */}
          <TabsContent value="content" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary" />
                  AI Content Generation Settings
                </CardTitle>
                <CardDescription>
                  Configure the AI model for generating agent content (names, descriptions, scenarios, etc.)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>AI Model</Label>
                    <Select
                      value={editedSettings.content_ai_model?.replace(/"/g, '') || ''}
                      onValueChange={(value) => updateSetting('content_ai_model', `"${value}"`)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select model" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableModels.map(model => (
                          <SelectItem key={model.id} value={model.id}>
                            <div className="flex items-center gap-2">
                              <span>{model.name}</span>
                              <Badge variant="outline" className="text-xs">{model.provider}</Badge>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Temperature: {editedSettings.content_temperature || 0.7}</Label>
                    <Slider
                      value={[parseFloat(editedSettings.content_temperature) || 0.7]}
                      onValueChange={([value]) => updateSetting('content_temperature', value.toString())}
                      min={0}
                      max={1}
                      step={0.1}
                    />
                    <p className="text-xs text-muted-foreground">
                      Higher temperature for more creative content generation
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Max Tokens</Label>
                  <Input
                    type="number"
                    value={editedSettings.content_max_tokens || 500}
                    onChange={(e) => updateSetting('content_max_tokens', e.target.value)}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Vision AI Settings */}
          <TabsContent value="vision" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5 text-primary" />
                  Vision Analysis Settings
                </CardTitle>
                <CardDescription>
                  Configure the AI model for analyzing video frames during simulations
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>AI Model</Label>
                    <Select
                      value={editedSettings.vision_ai_model?.replace(/"/g, '') || ''}
                      onValueChange={(value) => updateSetting('vision_ai_model', `"${value}"`)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select model" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableModels.filter(m => m.id.includes('gpt-4o') || m.id.includes('gemini')).map(model => (
                          <SelectItem key={model.id} value={model.id}>
                            <div className="flex items-center gap-2">
                              <span>{model.name}</span>
                              <Badge variant="outline" className="text-xs">{model.provider}</Badge>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Must be a multimodal model that supports vision
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Temperature: {editedSettings.vision_temperature || 0.3}</Label>
                    <Slider
                      value={[parseFloat(editedSettings.vision_temperature) || 0.3]}
                      onValueChange={([value]) => updateSetting('vision_temperature', value.toString())}
                      min={0}
                      max={1}
                      step={0.1}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Max Frames to Analyze per Session</Label>
                  <Input
                    type="number"
                    value={editedSettings.vision_max_frames || 30}
                    onChange={(e) => updateSetting('vision_max_frames', e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Higher values increase accuracy but also API costs
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* General Settings */}
          <TabsContent value="general" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings2 className="w-5 h-5 text-primary" />
                  General AI Settings
                </CardTitle>
                <CardDescription>
                  Configure system-wide AI behavior and fallback options
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>API Retry Attempts</Label>
                    <Input
                      type="number"
                      value={editedSettings.ai_retry_attempts || 3}
                      onChange={(e) => updateSetting('ai_retry_attempts', e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Number of times to retry failed API calls
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>API Timeout (seconds)</Label>
                    <Input
                      type="number"
                      value={editedSettings.ai_timeout_seconds || 60}
                      onChange={(e) => updateSetting('ai_timeout_seconds', e.target.value)}
                    />
                  </div>
                </div>

                <Separator />

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Enable Fallback AI Provider</Label>
                      <p className="text-sm text-muted-foreground">
                        Use a backup AI model if the primary fails
                      </p>
                    </div>
                    <Switch
                      checked={editedSettings.ai_fallback_enabled === 'true'}
                      onCheckedChange={(checked) => updateSetting('ai_fallback_enabled', checked.toString())}
                    />
                  </div>

                  {editedSettings.ai_fallback_enabled === 'true' && (
                    <div className="space-y-2 pl-4 border-l-2 border-muted">
                      <Label>Fallback AI Model</Label>
                      <Select
                        value={editedSettings.ai_fallback_model?.replace(/"/g, '') || ''}
                        onValueChange={(value) => updateSetting('ai_fallback_model', `"${value}"`)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select fallback model" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableModels.map(model => (
                            <SelectItem key={model.id} value={model.id}>
                              <div className="flex items-center gap-2">
                                <span>{model.name}</span>
                                <Badge variant="outline" className="text-xs">{model.provider}</Badge>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                <Separator />

                {/* Available Models Management */}
                <div className="space-y-4">
                  <Label className="text-lg font-semibold">Available AI Models</Label>
                  <p className="text-sm text-muted-foreground">
                    Manage the list of AI models available for selection throughout the system
                  </p>

                  <div className="space-y-2">
                    {availableModels.map((model, index) => (
                      <div key={model.id} className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg">
                        <GripVertical className="w-4 h-4 text-muted-foreground" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{model.name}</span>
                            <Badge variant="outline">{model.provider}</Badge>
                          </div>
                          <p className="text-xs text-muted-foreground">{model.id}</p>
                        </div>
                        <p className="text-sm text-muted-foreground max-w-xs">{model.description}</p>
                      </div>
                    ))}
                  </div>

                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      To add or remove models, contact your system administrator to update the database directly.
                    </AlertDescription>
                  </Alert>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Voice Agents Tab */}
          <TabsContent value="voice-agents" className="space-y-6">
            <VoiceAgentsManager />
          </TabsContent>
        </Tabs>
        
        {/* Save Revision Dialog */}
        <Dialog open={revisionDialogOpen} onOpenChange={setRevisionDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <BookmarkPlus className="h-5 w-5" />
                Save Revision
              </DialogTitle>
              <DialogDescription>
                Save the current prompt as a revision. You can optionally add a label to help identify this version later.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Label (optional)</Label>
                <Input
                  value={revisionLabel}
                  onChange={(e) => setRevisionLabel(e.target.value)}
                  placeholder="e.g., Before coaching update, Initial version..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setRevisionDialogOpen(false);
                setRevisionLabel('');
                setRevisionSettingKey(null);
              }}>
                Cancel
              </Button>
              <Button onClick={handleSaveRevision} className="gap-2">
                <Save className="h-4 w-4" />
                Save Revision
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

// Component for editing evaluation criteria
function EvaluationCriteriaEditor({
  criteria,
  onChange
}: {
  criteria: EvaluationCriterion[];
  onChange: (criteria: EvaluationCriterion[]) => void;
}) {
  const addCriterion = () => {
    onChange([
      ...criteria,
      { criterion: 'New Criterion', weight: 20, description: 'Description here' }
    ]);
  };

  const updateCriterion = (index: number, field: keyof EvaluationCriterion, value: any) => {
    const updated = [...criteria];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const removeCriterion = (index: number) => {
    onChange(criteria.filter((_, i) => i !== index));
  };

  const totalWeight = criteria.reduce((sum, c) => sum + c.weight, 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge variant={totalWeight === 100 ? 'default' : 'destructive'}>
            Total Weight: {totalWeight}%
          </Badge>
          {totalWeight !== 100 && (
            <span className="text-sm text-destructive">Should equal 100%</span>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={addCriterion}>
          <Plus className="w-4 h-4 mr-2" />
          Add Criterion
        </Button>
      </div>

      <div className="space-y-3">
        {criteria.map((criterion, index) => (
          <div key={index} className="p-4 border rounded-lg space-y-3">
            <div className="flex items-start gap-4">
              <div className="flex-1 space-y-2">
                <Input
                  value={criterion.criterion}
                  onChange={(e) => updateCriterion(index, 'criterion', e.target.value)}
                  placeholder="Criterion name"
                  className="font-medium"
                />
                <Textarea
                  value={criterion.description}
                  onChange={(e) => updateCriterion(index, 'description', e.target.value)}
                  placeholder="Description"
                  className="text-sm"
                  rows={2}
                />
              </div>
              <div className="w-24 space-y-2">
                <Label className="text-xs">Weight %</Label>
                <Input
                  type="number"
                  value={criterion.weight}
                  onChange={(e) => updateCriterion(index, 'weight', parseInt(e.target.value) || 0)}
                  min={0}
                  max={100}
                />
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="text-destructive hover:text-destructive"
                onClick={() => removeCriterion(index)}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Component for displaying and managing prompt revisions
function PromptRevisionPanel({
  revisions,
  onLoad,
  onDelete
}: {
  revisions: PromptRevision[];
  onLoad: (revisionId: string) => void;
  onDelete: (revisionId: string) => void;
}) {
  if (!revisions || revisions.length === 0) {
    return (
      <div className="mt-4 p-4 border border-dashed rounded-lg text-center">
        <History className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground">No revisions saved yet</p>
        <p className="text-xs text-muted-foreground mt-1">
          Click "Save Revision" to preserve the current prompt before making changes
        </p>
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-3">
      <div className="flex items-center gap-2">
        <History className="h-4 w-4 text-muted-foreground" />
        <Label className="text-sm font-medium">Saved Revisions ({revisions.length})</Label>
      </div>
      <ScrollArea className="h-48 border rounded-lg">
        <div className="p-2 space-y-2">
          {revisions.map((revision) => (
            <div 
              key={revision.id} 
              className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Clock className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                  <span className="text-sm font-medium">
                    {format(new Date(revision.savedAt), 'MMM d, yyyy h:mm a')}
                  </span>
                  {revision.label && (
                    <Badge variant="secondary" className="text-xs truncate max-w-32">
                      {revision.label}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1 truncate">
                  Saved by {revision.savedBy}
                </p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => onLoad(revision.id)}
                  className="gap-1"
                >
                  <RotateCcw className="h-3 w-3" />
                  Restore
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="h-8 w-8 text-destructive hover:text-destructive"
                  onClick={() => onDelete(revision.id)}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
